import { useEffect, useState } from 'react'
import { useResearchStore, isResearching } from '../../stores/researchStore'
import { isAIConfigured } from '../../stores/aiStore'
import { Button } from '../ui'
import type { Company } from '../../types'
import type { CompanyIntelligence as IntelligenceType, ResearchState } from '../../types/research'

interface CompanyIntelligenceProps {
  company: Company
}

export default function CompanyIntelligence({ company }: CompanyIntelligenceProps) {
  const { hydrate, isHydrated, getIntelligence, getResearchState, runResearch } = useResearchStore()

  const intelligence = getIntelligence(company.id)
  const researchState = getResearchState(company.id)
  const researching = isResearching(company.id)

  useEffect(() => {
    if (!isHydrated) {
      hydrate()
    }
  }, [isHydrated, hydrate])

  const handleResearch = () => {
    runResearch(company)
  }

  // Loading state
  if (!isHydrated) {
    return (
      <section className="pt-4 border-t border-border">
        <header className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-medium text-text-primary">Company Intelligence</h3>
        </header>
        <div className="animate-pulse space-y-2">
          <div className="h-4 bg-bg-secondary rounded w-3/4" />
          <div className="h-4 bg-bg-secondary rounded w-1/2" />
        </div>
      </section>
    )
  }

  // Researching state
  if (researching) {
    return (
      <section className="pt-4 border-t border-border">
        <header className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-medium text-text-primary">Company Intelligence</h3>
        </header>
        <ResearchProgress state={researchState} />
      </section>
    )
  }

  // No intelligence yet
  if (!intelligence) {
    return (
      <section className="pt-4 border-t border-border">
        <header className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-medium text-text-primary">Company Intelligence</h3>
        </header>
        <NoIntelligenceState 
          hasWebsite={!!(company.domain || company.website)} 
          aiConfigured={isAIConfigured()}
          onResearch={handleResearch}
        />
      </section>
    )
  }

  // Display intelligence
  return (
    <section className="pt-4 border-t border-border">
      <header className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-text-primary">Company Intelligence</h3>
        <Button variant="ghost" size="sm" onClick={handleResearch} disabled={researching}>
          <RefreshIcon />
          Refresh
        </Button>
      </header>
      
      <IntelligenceDisplay intelligence={intelligence} />
      
      {researchState.error && (
        <p className="text-xs text-error mt-2">{researchState.error}</p>
      )}
    </section>
  )
}

function ResearchProgress({ state }: { state: ResearchState }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-text-secondary">
        <Spinner />
        <span>
          {state.status === 'fetching-website' && 'Fetching website...'}
          {state.status === 'fetching-companies-house' && 'Checking Companies House...'}
          {state.status === 'generating-summary' && 'Generating summary...'}
        </span>
      </div>
      <div className="space-y-1">
        <ProgressItem label="Website" status={state.progress.website} />
        <ProgressItem label="Companies House" status={state.progress.companiesHouse} />
        <ProgressItem label="AI Summary" status={state.progress.ai} />
      </div>
      {state.error && (
        <p className="text-xs text-error mt-2">{state.error}</p>
      )}
    </div>
  )
}

function ProgressItem({ label, status }: { label: string; status: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {status === 'success' && <span className="text-success">✓</span>}
      {status === 'failed' && <span className="text-error">✗</span>}
      {status === 'skipped' && <span className="text-text-secondary">–</span>}
      {status === 'pending' && <span className="text-text-secondary">○</span>}
      <span className={status === 'pending' ? 'text-text-secondary' : 'text-text-primary'}>
        {label}
      </span>
    </div>
  )
}

function NoIntelligenceState({ 
  hasWebsite, 
  aiConfigured, 
  onResearch 
}: { 
  hasWebsite: boolean
  aiConfigured: boolean
  onResearch: () => void 
}) {
  if (!aiConfigured) {
    return (
      <div className="p-3 bg-bg-secondary rounded-md">
        <p className="text-xs text-text-secondary">
          Configure an AI provider in Settings to enable prospect research.
        </p>
      </div>
    )
  }

  return (
    <div className="p-3 bg-bg-secondary rounded-md">
      <p className="text-xs text-text-secondary mb-2">
        {hasWebsite 
          ? 'Run research to generate intelligence about this company.'
          : 'Add a website to enable research.'}
      </p>
      <Button variant="secondary" size="sm" onClick={onResearch}>
        Research
      </Button>
    </div>
  )
}

function IntelligenceDisplay({ intelligence }: { intelligence: IntelligenceType }) {
  const [expanded, setExpanded] = useState<string | null>('overview')

  return (
    <div className="space-y-3">
      {/* Confidence & timestamp */}
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <span>
          Last updated: {new Date(intelligence.generatedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </span>
        <ConfidenceBadge confidence={intelligence.confidence} />
      </div>

      {/* Overview */}
      <CollapsibleSection
        title="Overview"
        isExpanded={expanded === 'overview'}
        onToggle={() => setExpanded(expanded === 'overview' ? null : 'overview')}
      >
        <p className="text-sm text-text-primary leading-relaxed">{intelligence.overview}</p>
      </CollapsibleSection>

      {/* Firmographics */}
      {hasFirmographics(intelligence.firmographics) && (
        <CollapsibleSection
          title="Firmographics"
          isExpanded={expanded === 'firmographics'}
          onToggle={() => setExpanded(expanded === 'firmographics' ? null : 'firmographics')}
        >
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            {intelligence.firmographics.legalName && (
              <>
                <dt className="text-text-secondary">Legal name</dt>
                <dd className="text-text-primary">{intelligence.firmographics.legalName}</dd>
              </>
            )}
            {intelligence.firmographics.companyNumber && (
              <>
                <dt className="text-text-secondary">Company no.</dt>
                <dd className="text-text-primary">{intelligence.firmographics.companyNumber}</dd>
              </>
            )}
            {intelligence.firmographics.status && (
              <>
                <dt className="text-text-secondary">Status</dt>
                <dd className="text-text-primary capitalize">{intelligence.firmographics.status}</dd>
              </>
            )}
            {intelligence.firmographics.incorporatedDate && (
              <>
                <dt className="text-text-secondary">Incorporated</dt>
                <dd className="text-text-primary">
                  {formatDate(intelligence.firmographics.incorporatedDate)}
                </dd>
              </>
            )}
            {intelligence.firmographics.sector && (
              <>
                <dt className="text-text-secondary">Sector</dt>
                <dd className="text-text-primary">{intelligence.firmographics.sector}</dd>
              </>
            )}
            {intelligence.firmographics.registeredAddress && (
              <>
                <dt className="text-text-secondary">Address</dt>
                <dd className="text-text-primary col-span-1">{intelligence.firmographics.registeredAddress}</dd>
              </>
            )}
          </dl>
        </CollapsibleSection>
      )}

      {/* Fit Assessment */}
      {intelligence.fitAssessment && (
        <CollapsibleSection
          title="Fit Assessment"
          isExpanded={expanded === 'fit'}
          onToggle={() => setExpanded(expanded === 'fit' ? null : 'fit')}
        >
          <div className="space-y-3">
            {/* Fit score */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary">Fit score</span>
              <FitScoreIndicator score={intelligence.fitAssessment.fitScore} />
              {intelligence.fitAssessment.fitRationale && (
                <span className="text-xs text-text-secondary">
                  — {intelligence.fitAssessment.fitRationale}
                </span>
              )}
            </div>

            {/* Primary needs */}
            {intelligence.fitAssessment.primaryNeeds.length > 0 && (
              <div>
                <p className="text-xs text-text-secondary mb-1">Primary needs</p>
                <ul className="text-xs text-text-primary space-y-0.5">
                  {intelligence.fitAssessment.primaryNeeds.map((need, i) => (
                    <li key={i}>• {need}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Opportunities */}
            {intelligence.fitAssessment.opportunities.length > 0 && (
              <div>
                <p className="text-xs text-text-secondary mb-1">Opportunities</p>
                <ul className="text-xs text-text-primary space-y-0.5">
                  {intelligence.fitAssessment.opportunities.map((opp, i) => (
                    <li key={i}>• {opp}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Data sources */}
      <div className="pt-2 border-t border-border">
        <p className="text-xs text-text-secondary mb-1">Data sources</p>
        <div className="flex flex-wrap gap-2">
          {intelligence.sources.map((source) => (
            <span
              key={source.name}
              className={`text-xs px-1.5 py-0.5 rounded ${
                source.status === 'success'
                  ? 'bg-success/10 text-success'
                  : 'bg-bg-secondary text-text-secondary'
              }`}
            >
              {source.status === 'success' ? '✓' : '○'} {formatSourceName(source.name)}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function CollapsibleSection({
  title,
  isExpanded,
  onToggle,
  children,
}: {
  title: string
  isExpanded: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between bg-bg-secondary hover:bg-bg-secondary/80 transition-colors"
      >
        <span className="text-xs font-medium text-text-primary">{title}</span>
        <ChevronIcon expanded={isExpanded} />
      </button>
      {isExpanded && <div className="px-3 py-2 bg-bg-surface">{children}</div>}
    </div>
  )
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const colors: Record<string, string> = {
    high: 'bg-success/10 text-success',
    'medium-high': 'bg-success/10 text-success',
    medium: 'bg-warning/10 text-warning',
    low: 'bg-error/10 text-error',
  }

  return (
    <span className={`px-1.5 py-0.5 rounded text-xs ${colors[confidence] || colors.medium}`}>
      {confidence.replace('-', ' ')} confidence
    </span>
  )
}

function FitScoreIndicator({ score }: { score: number }) {
  const labels = {
    1: { text: 'Poor', className: 'bg-error/10 text-error' },
    2: { text: 'Weak', className: 'bg-warning/10 text-warning' },
    3: { text: 'Moderate', className: 'bg-text-secondary/10 text-text-secondary' },
    4: { text: 'Strong', className: 'bg-success/10 text-success' },
    5: { text: 'Perfect', className: 'bg-action-primary/10 text-action-primary' },
  } as const

  const label = labels[score as keyof typeof labels] ?? labels[3]

  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${label.className}`}>
      {label.text}
    </span>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

function RefreshIcon() {
  return (
    <svg className="w-3 h-3 mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 8a6 6 0 0110.89-3.48M14 8a6 6 0 01-10.89 3.48" />
      <path d="M14 3v4h-4M2 13V9h4" />
    </svg>
  )
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`w-3 h-3 text-text-secondary transition-transform ${expanded ? 'rotate-180' : ''}`}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  )
}

function hasFirmographics(f: IntelligenceType['firmographics']): boolean {
  return !!(f.legalName || f.companyNumber || f.status || f.incorporatedDate || f.sector || f.registeredAddress)
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function formatSourceName(name: string): string {
  const names: Record<string, string> = {
    website: 'Website',
    companiesHouse: 'Companies House',
  }
  return names[name] || name
}
